import { ApiService, type ApiRequestConfig } from "./api.service";
import type { TTimelineResponse } from "@/types/timeline-view";

/**
 * Work-item filters the timeline endpoints accept — the same param names and
 * comma-separated value semantics as the work-item list endpoints, so the
 * query built from the shared filter state can be sent as-is.
 */
export type TTimelineFilterParams = {
  /** Always sent: demo provenance must not imply a synced calendar. The backend
   *  still defaults it off for older bundles; retiring it is a backend change. */
  seeded_events_editable?: "true";
  /** Comma-separated WorkItemState ids. */
  state?: string;
  /** Priority option code (e.g. "high"). */
  priority?: string;
  /** Comma-separated assignee user ids. */
  assignees?: string;
  /** Comma-separated task-category (WorkspaceCategoryValue) ids — narrows tasks only. */
  task_category_ids?: string;
  /** Comma-separated event-category (WorkspaceCategoryValue) ids — narrows events only. */
  event_category_ids?: string;
  /** Comma-separated project ids — narrows both kinds. */
  project_ids?: string;
  /** Comma-separated ProjectState ids — keeps only items whose project is in one
   *  of those statuses. The workspace's CONFIGURABLE project status, never the
   *  derived on-track / overdue / over-capacity health band (NG-1637). */
  project_state_ids?: string;
  /** Comma-separated team ids — keeps only items whose Home team is one of them,
   *  matched exactly: a project's home team, or a project-less event's own
   *  (NG-3453). */
  home_team_ids?: string;
  /** Comma-separated team ids — keeps items with ANY of them among their
   *  delivery teams: a task's or project-linked event's project's, a standalone
   *  event's own Home team (NG-3457, NG-3458). */
  delivery_team_ids?: string;
  /** Case-insensitive substring match on the work-item name. */
  search?: string;
  /** Tri-state archived narrowing: "true" (archived only) / "false" (active
   *  only); omit to show both (the global/work-item timelines' default). */
  archived?: string;
  /** Inclusive `task.target_date` window (YYYY-MM-DD) — narrows tasks only. */
  target_date_after?: string;
  target_date_before?: string;
  /**
   * Repeatable custom-field filters, each formatted `<field_id>:<option_value>`
   * (select / multiselect only). Sent as repeated `custom_field` query params.
   */
  custom_field?: string[];
  /**
   * Which visibility rule the WORKSPACE timeline applies (NG-1804). `"list"`
   * asks for the same rows the Events list returns — including unassigned
   * items and items assigned outside the viewer's teams — instead of the
   * legacy team-scoped set. Omitted means the legacy rule.
   *
   * A bridge, not a filter: the workspace lists always send it, and the backend
   * still defaults to the legacy rule for older bundles. Retiring it is a
   * backend change. The project and work-item timelines ignore it.
   */
  visibility?: "list";
};

function buildTimelineParams(
  start: string,
  end: string,
  filters?: TTimelineFilterParams,
): URLSearchParams {
  const params = new URLSearchParams({ start, end });
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (Array.isArray(value)) {
      // Repeatable param (e.g. custom_field): one query entry per value.
      for (const entry of value) if (entry) params.append(key, entry);
    } else if (value) {
      params.set(key, value);
    }
  }
  return params;
}

export class TimelineService extends ApiService {
  async getTimeline(
    workspaceSlug: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
    config?: ApiRequestConfig,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(`/api/workspaces/${workspaceSlug}/timeline/?${params.toString()}`, config);
  }

  async getProjectTimeline(
    workspaceSlug: string,
    projectId: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
    config?: ApiRequestConfig,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/timeline/?${params.toString()}`,
      config,
    );
  }

  /** Subtree-scoped timeline: a work item plus its descendants (event detail tab). */
  async getWorkItemTimeline(
    workspaceSlug: string,
    workItemId: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
    config?: ApiRequestConfig,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(
      `/api/workspaces/${workspaceSlug}/work-items/${workItemId}/timeline/?${params.toString()}`,
      config,
    );
  }
}
